/**
 * Утилита для проверки версии приложения и автоматического обновления
 */

const VERSION_CHECK_INTERVAL = 2 * 60 * 1000; // Проверка каждые 2 минуты (было 5)
const CURRENT_VERSION_KEY = 'app_version_info';

interface VersionInfo {
  version: string;
  buildTime: string;
}

let checkInterval: number | null = null;

/**
 * Получить текущую версию с сервера
 */
async function fetchCurrentVersion(): Promise<VersionInfo | null> {
  try {
    // Добавляем timestamp чтобы обойти кэш браузера
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch version');
    }
    
    return await response.json();
  } catch (error) {
    console.warn('Failed to check version:', error);
    return null;
  }
}

/**
 * Сохранить текущую версию в localStorage
 */
function saveCurrentVersion(versionInfo: VersionInfo) {
  try {
    localStorage.setItem(CURRENT_VERSION_KEY, JSON.stringify(versionInfo));
  } catch (error) {
    console.warn('Failed to save version to localStorage:', error);
  }
}

/**
 * Получить сохраненную версию из localStorage
 */
function getSavedVersion(): VersionInfo | null {
  try {
    const saved = localStorage.getItem(CURRENT_VERSION_KEY);
    if (!saved) return null;
    return JSON.parse(saved);
  } catch (error) {
    console.warn('Failed to get version from localStorage:', error);
    return null;
  }
}

/**
 * Проверить наличие новой версии
 */
async function checkForNewVersion(): Promise<boolean> {
  const serverVersion = await fetchCurrentVersion();
  
  if (!serverVersion) {
    return false;
  }
  
  const savedVersion = getSavedVersion();
  
  // Если версия не сохранена, сохраняем текущую
  if (!savedVersion) {
    saveCurrentVersion(serverVersion);
    return false;
  }
  
  // Сравниваем версии и время сборки
  const isNewVersion = 
    serverVersion.version !== savedVersion.version || 
    serverVersion.buildTime !== savedVersion.buildTime;
  
  if (isNewVersion) {
    console.log('New version detected:', serverVersion.version, 'Build:', serverVersion.buildTime);
    return true;
  }
  
  return false;
}

/**
 * Обновить приложение (перезагрузить страницу)
 */
export function reloadApp() {
  // Очищаем все кэши
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => caches.delete(name));
    });
  }
  
  // Добавляем timestamp к URL для обхода кэша Telegram
  const url = new URL(window.location.href);
  url.searchParams.set('_cache_bust', Date.now().toString());
  url.searchParams.delete('tgWebAppData'); // Убираем старые Telegram параметры
  
  // Принудительная перезагрузка с новым URL
  window.location.href = url.toString();
}

/**
 * Показать уведомление о новой версии и автоматически обновить
 */
async function showUpdateNotification() {
  // Получаем информацию о новой версии с сервера
  const serverVersion = await fetchCurrentVersion();
  
  if (!serverVersion) return;
  
  console.log('🔄 New version detected, auto-updating...', serverVersion);
  
  // Показываем собственное уведомление
  const notification = document.createElement('div');
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #4CAF50;
    color: white;
    padding: 12px 24px;
    border-radius: 8px;
    z-index: 10000;
    font-family: system-ui, -apple-system, sans-serif;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  `;
  // Dynamic import to avoid circular dependency at module level
  const getUpdateText = async () => {
    try {
      const { default: i18n } = await import('../i18n');
      return `🔄 ${i18n.t('common:notification.appUpdating')}`;
    } catch { return '🔄 Updating...'; }
  };
  notification.textContent = await getUpdateText();
  document.body.appendChild(notification);
  
  // Обновляем через 1 секунду
  setTimeout(() => {
    saveCurrentVersion(serverVersion);
    reloadApp();
  }, 1000);
}

/**
 * Запустить автоматическую проверку версии
 */
export function startVersionCheck() {
  // Проверяем сразу при запуске
  checkForNewVersion().then(async hasUpdate => {
    if (hasUpdate) {
      await showUpdateNotification();
    }
  });
  
  // Запускаем периодическую проверку
  if (checkInterval) {
    clearInterval(checkInterval);
  }
  
  checkInterval = setInterval(async () => {
    const hasUpdate = await checkForNewVersion();
    if (hasUpdate) {
      await showUpdateNotification();
    }
  }, VERSION_CHECK_INTERVAL);
}

/**
 * Остановить автоматическую проверку версии
 */
export function stopVersionCheck() {
  if (checkInterval) {
    clearInterval(checkInterval);
    checkInterval = null;
  }
}

/**
 * Простой debounce helper
 */
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: number | null = null;
  return function (...args: Parameters<T>) {
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Инициализировать систему версионирования
 */
export async function initVersionCheck() {
  // Сохраняем текущую версию при первом запуске
  const serverVersion = await fetchCurrentVersion();
  if (serverVersion) {
    saveCurrentVersion(serverVersion);
  }
  
  // Запускаем автоматическую проверку
  startVersionCheck();
  
  // Добавляем проверку при возврате в приложение (focus)
  if (typeof window !== 'undefined') {
    // Debounce focus event (1 секунда)
    const debouncedFocusCheck = debounce(async () => {
      const hasUpdate = await checkForNewVersion();
      if (hasUpdate) {
        await showUpdateNotification();
      }
    }, 1000);
    
    window.addEventListener('focus', debouncedFocusCheck);
    
    // Для Telegram WebApp: проверяем при показе viewport
    const tg = (window as any).Telegram?.WebApp;
    if (tg) {
      // Debounce viewport event (2 секунды) - срабатывает при каждом скролле
      const debouncedViewportCheck = debounce(async () => {
        if (tg.isExpanded) {
          const hasUpdate = await checkForNewVersion();
          if (hasUpdate) {
            await showUpdateNotification();
          }
        }
      }, 2000);
      
      tg.onEvent('viewportChanged', debouncedViewportCheck);
    }
  }
}

